/**
 * SlideStage.jsx — a slide drawn from slideCells(): the same rectangles the
 * export renders into, so what you frame is exactly what saves.
 *
 * Each cell holds a photo layer (.cellimg): a plain CSS background, no canvas and
 * no WebGL context per cell. The adjustments are already baked into the preview
 * (previews.js -> pipeline.js), so nothing here filters. Only the 480px thumbnail
 * a cell shows before its preview lands still carries the cheap CSS
 * approximation, so the cell does not flash ungraded on the way in.
 *
 * Interactive stages (the slide cards) carry data-si / data-ci (and data-gid on
 * panorama parts) for the delegated pan, zoom, drop and pick handlers in
 * CarouselView. `graded` is what decides whether a stage paints the real preview
 * or the thumbnail approximation, and it defaults to `interactive` — but the
 * export sheet is not interactive and IS the confirmation step, so it asks for
 * graded explicitly (useSlidePreviews below makes the previews it needs).
 */

import { Fragment, memo, useEffect, useRef, useSyncExternalStore } from 'react';
import { updatePhotoDims } from '../../hooks/useCarouselStore';
import {
  cellFit, cellImgId, cellLookAt, cellPlacement, effectiveCell, formatSize, slideCells, slideGeom,
} from '../../lib/carousel/geometry.js';
import { cellLook, filterString, lookKey, viewPhoto } from '../../lib/carousel/look.js';
import { getPreview, requestPreview, subscribePreviews } from '../../lib/carousel/previews.js';

// Background size/position for a photo in a cell of rc.w x rc.h slide pixels.
// `fit` 'contain' letterboxes the whole photo; CSS percentage positioning places
// it in the free space exactly as cellPlacement does.
export function cellBackground(im, cell, rc, fit) {
  const pl = cellPlacement(im, rc.w, rc.h, cell, fit || 'fill');
  const size = pl.mode === 'contain'
    ? (pl.dw / rc.w * 100) + '% ' + (pl.dh / rc.h * 100) + '%'
    // both axes from the photo's own w/h, so a thumbnail's rounded height can't stretch the frame
    : (im.w / pl.src.sw * 100) + '% ' + (im.h / pl.src.sh * 100) + '%';
  return {
    backgroundSize: size,
    backgroundPosition: (cell.ox * 100) + '% ' + (cell.oy * 100) + '%',
  };
}

// Everything a cell's photo layer needs: placement under its look. The look is
// the photo's unless the cell opted out. A baked preview already carries the
// adjustments; the thumbnail does not, so that one case gets the approximation.
export function cellLayerStyle(im0, cell, rc, stored, url) {
  const look = cellLook(im0, stored);
  const im = viewPhoto(im0, look);
  const fit = stored && stored.fit === 'contain' ? 'contain' : 'fill';
  const filter = url === im0.thumb ? filterString(look.adj) : '';
  return {
    backgroundImage: 'url("' + url + '")',
    ...cellBackground(im, cell, rc, fit),
    ...(filter ? { filter } : null),
  };
}

// Repaints every visible part of a panorama group mid-drag, without a render.
export function paintPanoGroup(host, gid, group, slides, imgById, design) {
  const [W, H] = formatSize(design.fmt);
  const panos = { [gid]: group };
  host.querySelectorAll('.cell[data-gid="' + gid + '"]').forEach((el) => {
    const sl = slides[+el.dataset.si];
    if (!sl) return;
    const cell = effectiveCell(sl, 0, panos, imgById, W, H, design);
    const im0 = cell && imgById(cell.imgId);
    if (!im0) return;
    const im = viewPhoto(im0, cellLookAt(sl, im0, 0));
    const g = slideGeom(sl, design);
    const bg = cellBackground(im, cell, slideCells(sl, W, H, g.pad, g.gap)[0], 'fill');
    const layer = el.querySelector('.cellimg') || el;
    layer.style.backgroundSize = bg.backgroundSize;
    layer.style.backgroundPosition = bg.backgroundPosition;
  });
}

/**
 * The graded previews a list of slides needs, requested and subscribed to in one
 * place. Returns a string that changes when one lands, for passing to the stages
 * as `previews` so a memoized card repaints.
 *
 * `on` is for a sheet that is mounted but closed: previews are ~1600px blobs made
 * from the originals, so a closed export dialog must not start rendering the whole
 * carousel behind the library.
 */
export function useSlidePreviews(slides, panos, imgById, on = true) {
  const wants = [];
  if (on) {
    slides.forEach((sl) => {
      sl.cells.forEach((_, ci) => {
        const id = cellImgId(sl, ci, panos, imgById);
        const im = id ? imgById(id) : null;
        if (im) wants.push({ im, look: cellLookAt(sl, im, ci) });
      });
    });
  }
  const key = wants.map((w) => w.im.id + '@' + lookKey(w.look)).join('|');
  const ref = useRef(wants);
  ref.current = wants;
  const pv = useSyncExternalStore(
    subscribePreviews,
    () => ref.current.map((w) => getPreview(w.im.id, w.look) || '').join('|'),
  );
  useEffect(() => {
    key.split('|').forEach((_, i) => {
      const w = ref.current[i];
      if (w) requestPreview(w.im, w.look, updatePhotoDims);
    });
  }, [key]);
  return pv;
}

function SlideStage({ sl, si, design, group, imgById, interactive, graded = interactive }) {
  const [W, H] = formatSize(design.fmt);
  const g = slideGeom(sl, design);
  const rects = slideCells(sl, W, H, g.pad, g.gap);
  const panos = group && sl.pano ? { [sl.pano.gid]: group } : null;

  return (
    <div
      className={'stage ' + sl.layout + (sl.pano ? ' pano' : '')}
      style={{ aspectRatio: W + ' / ' + H, background: design.bg }}
    >
      {sl.cells.map((stored, ci) => {
        const rc = rects[ci] || rects[0];
        const pct = (r) => ({
          left: (r.x / W * 100) + '%',
          top: (r.y / H * 100) + '%',
          width: (r.w / W * 100) + '%',
          height: (r.h / H * 100) + '%',
        });
        const box = pct(rc);
        // the fill ring an overlapping frame sits on, laid down before its photo:
        // the export fills the same grown rectangle, so the two agree
        const mat = rc.mat
          ? <span className="cc-mat" style={{ ...pct({ x: rc.x - rc.mat, y: rc.y - rc.mat, w: rc.w + 2 * rc.mat, h: rc.h + 2 * rc.mat }), background: design.bg }} />
          : null;
        const cell = effectiveCell(sl, ci, panos, imgById, W, H, design);
        const im = cell && imgById(cell.imgId);
        const data = interactive
          ? { 'data-si': si, 'data-ci': ci, ...(sl.pano && ci === 0 ? { 'data-gid': sl.pano.gid } : {}) }
          : {};
        if (!im) {
          return (
            <Fragment key={ci}>
              {mat}
              <div className="cell empty" style={box} {...data}>
                {interactive && <span>+</span>}
              </div>
            </Fragment>
          );
        }
        const look = cellLookAt(sl, im, ci);
        const url = (graded && getPreview(im.id, look)) || im.thumb;
        const fitClass = cellFit(sl, stored) === 'contain' ? ' contain' : '';
        return (
          <Fragment key={ci}>
            {mat}
            <div className={'cell' + fitClass} style={box} {...data}>
              <div className="cellimg" style={cellLayerStyle(im, cell, rc, sl.pano ? null : stored, url)} />
              {interactive && <div className="zoomtag">{Math.round(cell.zoom * 100) + '%'}</div>}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

export default memo(SlideStage);
